import { useSearchParams } from 'react-router-dom';

import {
  useMutation,
  type UseMutationOptions,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { type AxiosResponse } from 'axios';

import {
  activateBaseScenario,
  type ActivateBaseScenarioInput,
  activateScenario,
  type ActivateScenarioInput,
  cloneScenario,
  type CloneScenarioInput,
  createScenario,
  type CreateScenarioInput,
  deleteScenarioById,
  type DeleteScenarioInput,
  getActiveScenarioByDataTableName,
  type GetActiveScenarioByDataTableNameInput,
  getActiveScenarios,
  getBaseScenario,
  getBaseScenarioData,
  type GetBaseScenarioDataInput,
  type GetBaseScenarioInput,
  getScenarioById,
  type GetScenarioByIdInput,
  getScenarioData,
  type GetScenarioDataInput,
  getScenarioListDisplay,
  type GetScenarioListDisplayInput,
  getScenarios,
  getScenariosByDataTableName,
  type GetScenariosByDataTableNameInput,
  type GetScenariosInput,
  getScenarioVectors,
  type GetScenarioVectorsInput,
  updateScenarioById,
  type UpdateScenarioByIdInput,
} from '../../api/scenarios';
import {
  type TypeModel,
  type TypeScenario,
  type TypeScenarioListDisplay,
  type TypeScenarioVector,
} from '../../common/dataTypes';
import { type DefaultMutationError, type DefaultQueryError } from '../index';

export const useCreateScenario = (
  opts?: Partial<
    UseMutationOptions<Awaited<TypeScenario>, DefaultMutationError, any, any>
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['createScenario'],
    mutationFn: async (input: CreateScenarioInput) => {
      const { data }: AxiosResponse<TypeScenario> = await createScenario(input);

      return data;
    },
    onSuccess: async (data, variables, unknown, context) => {
      await queryClient.invalidateQueries({
        queryKey: [
          'getScenarioListDisplay',
          {
            orgId: variables.orgId,
            projectId: variables.projectId,
            modelId: variables.modelId,
          },
        ],
      });
      await queryClient.invalidateQueries({
        queryKey: [
          'getScenariosByDataTableName',
          {
            orgId: variables.orgId,
            projectId: variables.projectId,
            modelId: variables.modelId,
            dataTableName: variables.data.data_table_name,
          },
        ],
      });
      opts?.onSuccess?.(data, variables, unknown, context);
    },
    onError(error, variables, unknown, context) {
      opts?.onError?.(
        error as unknown as DefaultMutationError,
        variables,
        unknown,
        context
      );
    },
  });
};

export const useUpdateScenarioById = (
  opts?: Partial<
    UseMutationOptions<Awaited<TypeModel>, DefaultMutationError, any, any>
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['useUpdateScenarioById'],
    mutationFn: async (input: UpdateScenarioByIdInput) => {
      const { data }: AxiosResponse<TypeModel> =
        await updateScenarioById(input);

      return data;
    },
    onSuccess: async (data, variables, unknown, context) => {
      await queryClient.refetchQueries({
        queryKey: ['getScenarioById'],
      });
      await queryClient.refetchQueries({
        queryKey: ['getScenarios'],
      });
      await queryClient.invalidateQueries({
        queryKey: [
          'getScenariosByDataTableName',
          {
            orgId: variables.orgId,
            projectId: variables.projectId,
            modelId: variables.modelId,
            dataTableName: variables.data.data_table_name,
          },
        ],
      });
      opts?.onSuccess?.(data, variables, unknown, context);
    },
    onError(error, variables, unknown, context) {
      opts?.onError?.(
        error as unknown as DefaultMutationError,
        variables,
        unknown,
        context
      );
    },
  });
};

export const useDeleteScenarioById = (
  opts?: Partial<
    UseMutationOptions<Awaited<AxiosResponse>, DefaultMutationError, any, any>
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['deleteScenarioById'],
    mutationFn: async (input: DeleteScenarioInput) => {
      const res: AxiosResponse = await deleteScenarioById(input);

      return res;
    },
    onSuccess: async (data, variables, unknown, context) => {
      await queryClient.refetchQueries({
        queryKey: ['getScenariosByDataTableName'],
      });
      opts?.onSuccess?.(data, variables, unknown, context);
    },
    onError(error, variables, unknown, context) {
      opts?.onError?.(
        error as unknown as DefaultMutationError,
        variables,
        unknown,
        context
      );
    },
  });
};

export const useGetScenarioData = (
  input: GetScenarioDataInput,
  opts?: Partial<
    UseQueryOptions<
      Awaited<Record<string, (string | number)[]>>,
      DefaultQueryError
    >
  >
) =>
  useQuery({
    queryKey: ['getScenarioData', input],
    queryFn: async () => {
      const { data }: AxiosResponse<Record<string, (string | number)[]>> =
        await getScenarioData(input);

      return data;
    },
    ...opts,
  });

export const useGetScenarioListDisplay = (
  input: GetScenarioListDisplayInput,
  opts?: Partial<
    UseQueryOptions<Awaited<TypeScenarioListDisplay>, DefaultQueryError>
  >
) =>
  useQuery({
    queryKey: ['getScenarioListDisplay', input],
    queryFn: async () => {
      const { data }: AxiosResponse<TypeScenarioListDisplay> =
        await getScenarioListDisplay(input);

      return data;
    },
    ...opts,
  });

export const useGetBaseScenarioData = (
  input: GetBaseScenarioDataInput,
  opts?: Partial<
    UseQueryOptions<
      Awaited<Record<string, (string | number)[]>>,
      DefaultQueryError
    >
  >
) =>
  useQuery({
    queryKey: ['getBaseScenarioData', input],
    queryFn: async () => {
      const { data }: AxiosResponse<Record<string, (string | number)[]>> =
        await getBaseScenarioData(input);

      return data;
    },
    ...opts,
  });

export const useGetScenarioVectors = (
  input: GetScenarioVectorsInput,
  opts?: Partial<
    UseQueryOptions<Awaited<TypeScenarioVector[]>, DefaultQueryError>
  >
) =>
  useQuery({
    queryKey: ['getScenarioVectors', input],
    queryFn: async () => {
      const { data }: AxiosResponse<TypeScenarioVector[]> =
        await getScenarioVectors(input);

      return data;
    },
    ...opts,
  });

export const useGetScenarios = (
  input: GetScenariosInput,
  opts?: Partial<UseQueryOptions<Awaited<TypeScenario[]>, DefaultQueryError>>
) =>
  useQuery({
    queryKey: ['getScenarios', input],
    queryFn: async () => {
      const { data }: AxiosResponse<TypeScenario[]> = await getScenarios(input);

      return data;
    },
    ...opts,
  });

export const useGetActiveScenarios = (
  input: GetScenariosInput,
  opts?: Partial<UseQueryOptions<Awaited<TypeScenario[]>, DefaultQueryError>>
) =>
  useQuery({
    queryKey: ['getActiveScenarios', input],
    queryFn: async () => {
      const { data }: AxiosResponse<TypeScenario[]> =
        await getActiveScenarios(input);

      return data;
    },
    ...opts,
  });

export const useGetScenariosByDataTableName = (
  input: GetScenariosByDataTableNameInput,
  opts?: Partial<UseQueryOptions<Awaited<TypeScenario[]>, DefaultQueryError>>
) =>
  useQuery({
    queryKey: ['getScenariosByDataTableName', input],
    queryFn: async () => {
      const { data }: AxiosResponse<TypeScenario[]> =
        await getScenariosByDataTableName(input);

      return data;
    },
    ...opts,
  });

export const useGetActiveScenarioByDataTableName = (
  input: GetActiveScenarioByDataTableNameInput,
  opts?: Partial<UseQueryOptions<Awaited<TypeScenario>, DefaultQueryError>>
) =>
  useQuery({
    queryKey: ['getActiveScenarioByDataTableName', input],
    queryFn: async () => {
      const { data }: AxiosResponse<TypeScenario[]> =
        await getActiveScenarioByDataTableName(input);

      return data[0];
    },
    ...opts,
  });

export const useGetScenarioById = (
  input: GetScenarioByIdInput,
  opts?: Partial<UseQueryOptions<Awaited<TypeScenario>, DefaultQueryError>>
) =>
  useQuery({
    queryKey: ['getScenarioById', input],
    queryFn: async () => {
      const { data }: AxiosResponse<TypeScenario> =
        await getScenarioById(input);

      return data;
    },
    ...opts,
  });

export const useGetBaseScenario = (
  input: GetBaseScenarioInput,
  opts?: Partial<UseQueryOptions<Awaited<TypeScenario>, DefaultQueryError>>
) =>
  useQuery({
    queryKey: ['getBaseScenario', input],
    queryFn: async () => {
      const { data }: AxiosResponse<TypeScenario> =
        await getBaseScenario(input);

      return data;
    },
    ...opts,
  });

export const useCloneScenario = (
  opts?: Partial<
    UseMutationOptions<Awaited<TypeScenario>, DefaultMutationError, any, any>
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['cloneModel'],
    mutationFn: async (input: CloneScenarioInput) => {
      const { data } = await cloneScenario(input);

      return data;
    },
    onSuccess: async (data, variables, unknown, context) => {
      await queryClient.refetchQueries({
        queryKey: ['getScenarios'],
      });
      opts?.onSuccess?.(data, variables, unknown, context);
    },
  });
};

export const useActivateScenario = (
  opts?: Partial<
    UseMutationOptions<Awaited<TypeScenario>, DefaultMutationError, any, any>
  >
) => {
  const queryClient = useQueryClient();

  const [searchParams, setSearchParams] = useSearchParams();

  return useMutation({
    mutationKey: ['activateScenario'],
    mutationFn: async (input: ActivateScenarioInput) => {
      const { data } = await activateScenario(input);

      return data;
    },
    onSuccess: async (data, variables, unknown, context) => {
      searchParams.delete('isActualData');
      setSearchParams(searchParams);

      await Promise.all([
        queryClient.refetchQueries({
          queryKey: [
            'getScenarioById',
            {
              orgId: variables.orgId,
              projectId: variables.projectId,
              modelId: variables.modelId,
            },
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            'getScenarioListDisplay',
            {
              orgId: variables.orgId,
              projectId: variables.projectId,
              modelId: variables.modelId,
            },
          ],
        }),
        queryClient.refetchQueries({
          queryKey: [
            'getBaseScenario',
            {
              orgId: variables.orgId,
              projectId: variables.projectId,
              modelId: variables.modelId,
              dataTableName: variables.dataTableName,
            },
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            'getModelOutputWithActualData',
            {
              orgId: variables.orgId,
              projectId: variables.projectId,
              modelId: variables.modelId,
            },
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            'getModelOutput',
            {
              orgId: variables.orgId,
              projectId: variables.projectId,
              modelId: variables.modelId,
            },
          ],
        }),
        queryClient.refetchQueries({
          queryKey: [
            'getScenariosByDataTableName',
            {
              orgId: variables.orgId,
              projectId: variables.projectId,
              modelId: variables.modelId,
              dataTableName: variables.dataTableName,
            },
          ],
        }),
      ]);

      opts?.onSuccess?.(data, variables, unknown, context);
    },
  });
};

export const useActivateBaseScenario = (
  opts?: Partial<
    UseMutationOptions<Awaited<TypeScenario>, DefaultMutationError, any, any>
  >
) => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  return useMutation({
    mutationKey: ['activateBaseScenario'],
    mutationFn: async (input: ActivateBaseScenarioInput) => {
      const { data } = await activateBaseScenario(input);

      return data;
    },
    onSuccess: async (data, variables, unknown, context) => {
      searchParams.delete('isActualData');
      setSearchParams(searchParams);

      await Promise.all([
        queryClient.refetchQueries({
          queryKey: ['getBaseScenario', variables],
        }),
        queryClient.refetchQueries({
          queryKey: [
            'getScenarioById',
            {
              orgId: variables.orgId,
              projectId: variables.projectId,
              modelId: variables.modelId,
            },
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            'getScenarioListDisplay',
            {
              orgId: variables.orgId,
              projectId: variables.projectId,
              modelId: variables.modelId,
            },
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            'getModelOutput',
            {
              orgId: variables.orgId,
              projectId: variables.projectId,
              modelId: variables.modelId,
            },
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            'getModelOutputWithActualData',
            {
              orgId: variables.orgId,
              projectId: variables.projectId,
              modelId: variables.modelId,
            },
          ],
        }),
        queryClient.refetchQueries({
          queryKey: ['getScenariosByDataTableName', variables],
        }),
      ]);

      opts?.onSuccess?.(data, variables, unknown, context);
    },
  });
};
